import type { ConfiguratorModuleId } from "../content/configurator";
import type { PreviewPageId, SelectablePageId } from "./configuratorPageCatalog";
import type { ConfiguratorState } from "./configuratorState";

/** Full page content delivered by step-2 page selection (not a separate step-3 toggle). */
export const PAGE_OWNED_MODULE: Partial<Record<SelectablePageId, ConfiguratorModuleId>> = {
  about: "about-page",
  gallery: "project-gallery",
  pricing: "pricing",
  booking: "booking",
  contact: "contact-form",
  faq: "faq",
  blog: "blog",
};

/** Modules that can embed on pages beyond their dedicated page — shown in step 3. */
export const EMBED_CAPABLE_MODULES: ConfiguratorModuleId[] = [
  "faq",
  "blog",
  "contact-form",
];

/** Auto-enable embed module when its page is selected (for use on product detail, home, etc.). */
export const PAGE_EMBED_AUTO_ENABLE: Partial<Record<SelectablePageId, ConfiguratorModuleId>> = {
  faq: "faq",
  blog: "blog",
};

export function getEmbedModuleForPage(pageId: SelectablePageId): ConfiguratorModuleId | undefined {
  return PAGE_EMBED_AUTO_ENABLE[pageId];
}

export function isPageOwnedModule(moduleId: ConfiguratorModuleId): boolean {
  return Object.values(PAGE_OWNED_MODULE).includes(moduleId);
}

export function hasSelectedPage(state: ConfiguratorState, pageId: SelectablePageId): boolean {
  return state.selectedPages.includes(pageId);
}

export function hasEmbedModule(state: ConfiguratorState, moduleId: ConfiguratorModuleId): boolean {
  return state.modules.includes(moduleId);
}

/** Dedicated FAQ page is on when the FAQ page is selected in step 2. */
export function hasFaqPage(state: ConfiguratorState): boolean {
  return hasSelectedPage(state, "faq");
}

/** FAQ accordion blocks on non-FAQ pages (product detail, home, pricing…). */
export function hasFaqEmbed(state: ConfiguratorState): boolean {
  return hasEmbedModule(state, "faq");
}

export function hasBlogPage(state: ConfiguratorState): boolean {
  return hasSelectedPage(state, "blog");
}

export function hasBlogFeed(state: ConfiguratorState): boolean {
  return hasEmbedModule(state, "blog");
}

const FAQ_EMBED_PAGES: PreviewPageId[] = ["home", "product-detail", "pricing", "contact", "shop"];

export function showFaqEmbedOnPage(state: ConfiguratorState, pageId: PreviewPageId): boolean {
  if (pageId === "faq") return false;
  return hasFaqEmbed(state) && FAQ_EMBED_PAGES.includes(pageId);
}

const BLOG_FEED_PAGES: PreviewPageId[] = ["home", "about", "shop", "product-detail"];

export function showBlogFeedOnPage(state: ConfiguratorState, pageId: PreviewPageId): boolean {
  if (pageId === "blog" || pageId === "blog-detail") return false;
  return hasBlogFeed(state) && BLOG_FEED_PAGES.includes(pageId);
}
